/**
 * Członkowie rodziny zgłoszeni do ubezpieczenia zdrowotnego (Etap 30, ADR-0031).
 *
 * Karta w zakładce „Dane osobowe" akt pracownika. Zgłoszenie na formularzu
 * ZUS ZCNA składa PŁATNIK w ciągu 7 dni od dnia, w którym pracownik go o tym
 * poinformował — dlatego formularz ma dwie osobne daty: dzień uzyskania
 * uprawnień i dzień zgłoszenia sprawy. To nie ta sama data i nie wolno jej
 * zgadywać.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Modal,
  Select,
  Spinner,
  useToast,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { DISABILITY_DEGREES, KINSHIP_CODES } from '@k1p4/shared'
import { ApiError, api } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface FamilyMembersProps {
  employeeId: string
  /** Dodawanie i zgłaszanie utraty uprawnień wymaga uprawnienia „Kadry : Edycja". */
  canEdit: boolean
}

interface AddForm {
  firstName: string
  lastName: string
  pesel: string
  birthDate: string
  kinshipCode: string
  sharedHousehold: boolean
  disabilityCode: string
  entitlementFrom: string
  declaredAt: string
  addressPostalCode: string
  addressCity: string
  addressStreet: string
  addressHouseNo: string
  addressFlatNo: string
}

const EMPTY: AddForm = {
  firstName: '',
  lastName: '',
  pesel: '',
  birthDate: '',
  kinshipCode: '',
  sharedHousehold: true,
  disabilityCode: '0',
  entitlementFrom: '',
  declaredAt: '',
  addressPostalCode: '',
  addressCity: '',
  addressStreet: '',
  addressHouseNo: '',
  addressFlatNo: '',
}

export function FamilyMembers({ employeeId, canEdit }: FamilyMembersProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    firstName: useId(),
    lastName: useId(),
    pesel: useId(),
    birthDate: useId(),
    kinship: useId(),
    disability: useId(),
    from: useId(),
    declared: useId(),
    postalCode: useId(),
    city: useId(),
    street: useId(),
    houseNo: useId(),
    flatNo: useId(),
    endDate: useId(),
    endDeclared: useId(),
  }

  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<AddForm>(EMPTY)
  const [ending, setEnding] = useState<{ id: string; name: string } | null>(null)
  const [endForm, setEndForm] = useState({ entitlementTo: '', declaredAt: '' })
  const [error, setError] = useState<string | null>(null)

  const members = useQuery({
    queryKey: ['family', employeeId],
    queryFn: () => api.familyMembers(employeeId),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['family', employeeId] })
    /* Nowe zgłoszenie zmienia listę „do zrobienia" w module ZUS/PIT/PPK. */
    void queryClient.invalidateQueries({ queryKey: ['zus-registrations'] })
  }

  const add = useMutation({
    mutationFn: () => {
      const optional = (value: string) => (value.trim() === '' ? undefined : value.trim())
      const address =
        form.addressPostalCode.trim() === ''
          ? undefined
          : {
              postalCode: form.addressPostalCode.trim(),
              city: form.addressCity.trim(),
              ...(optional(form.addressStreet) ? { street: form.addressStreet.trim() } : {}),
              houseNo: form.addressHouseNo.trim(),
              ...(optional(form.addressFlatNo) ? { flatNo: form.addressFlatNo.trim() } : {}),
            }
      return api.addFamilyMember(employeeId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        ...(optional(form.pesel) ? { pesel: form.pesel.trim() } : {}),
        ...(optional(form.birthDate) ? { birthDate: form.birthDate.trim() } : {}),
        kinshipCode: form.kinshipCode,
        sharedHousehold: form.sharedHousehold,
        disabilityCode: form.disabilityCode,
        entitlementFrom: form.entitlementFrom,
        declaredAt: form.declaredAt,
        ...(address ? { address } : {}),
      })
    },
    onSuccess: () => {
      toast.show(t('family.added'), { tone: 'success' })
      setAddOpen(false)
      setForm(EMPTY)
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'MEMBER_ID_REQUIRED'
          ? 'family.errorIdentifier'
          : caught instanceof ApiError && caught.code === 'MEMBER_BIRTH_DATE_REQUIRED'
            ? 'family.errorBirthDate'
            : caught instanceof ApiError && caught.code === 'NO_HEALTH_INSURANCE_TITLE'
              ? 'family.notInsuredHr'
              : 'family.errorGeneric',
      )
      /* Tytuł mógł się zmienić od wczytania karty — lista i flaga idą od nowa. */
      if (caught instanceof ApiError && caught.code === 'NO_HEALTH_INSURANCE_TITLE') refresh()
    },
  })

  const end = useMutation({
    mutationFn: () =>
      api.endFamilyMember(ending!.id, {
        entitlementTo: endForm.entitlementTo,
        declaredAt: endForm.declaredAt,
      }),
    onSuccess: () => {
      toast.show(t('family.ended'), { tone: 'success' })
      setEnding(null)
      setEndForm({ entitlementTo: '', declaredAt: '' })
      setError(null)
      refresh()
    },
    onError: (caught) => {
      setError(
        caught instanceof ApiError && caught.code === 'BEFORE_START'
          ? 'family.errorBeforeStart'
          : 'family.errorGeneric',
      )
    },
  })

  const items = members.data?.items ?? []
  const addReady =
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.kinshipCode !== '' &&
    form.entitlementFrom !== '' &&
    form.declaredAt !== ''

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('family.title')}</h3>
        {canEdit ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={members.data?.insured === false}
            onClick={() => {
              setError(null)
              setAddOpen(true)
            }}
          >
            {t('family.add')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('family.lead')}</p>
      {/*
        Osoba bez tytułu do ubezpieczeń u płatnika (grupa 166): wpisy już istniejące
        zostają, ale nowego członka rodziny się nie zgłasza — ZCNA nie powstanie.
      */}
      {members.data?.insured === false ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('family.notInsuredHr')}
        </InfoBanner>
      ) : null}

      {error !== null && !addOpen && ending === null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {members.isPending ? (
        <Spinner label={t('family.loading')} />
      ) : items.length === 0 ? (
        /* Bez tytułu baner mówi, kto zgłasza — pusty stan nie każe dodawać (weryfikacja Fali 2). */
        members.data?.insured === false ? null : (
          <EmptyState title={t('family.empty')} description={t('family.emptyBody')} />
        )
      ) : (
        <Card pad="flush" className={styles.list}>
          {items.map((member) => (
            <div key={member.id} className={styles.row}>
              <span className={styles.rowName}>
                {member.firstName} {member.lastName}
              </span>
              <span className={styles.rowMeta}>
                {t(`family.kinship.${member.kinshipCode}`, { defaultValue: member.kinshipName ?? member.kinshipCode })}
                {member.pesel ? ` · ${member.pesel}` : ''}
                {member.sharedHousehold ? ` · ${t('family.sharedHousehold')}` : ''}
              </span>
              <span className={styles.rowDates}>
                {t('family.from', { date: formatDate(member.entitlementFrom, 'dot') })}
                {member.entitlementTo
                  ? ` · ${t('family.to', { date: formatDate(member.entitlementTo, 'dot') })}`
                  : ''}
              </span>
              {canEdit && member.entitlementTo === null ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setError(null)
                    setEnding({ id: member.id, name: `${member.firstName} ${member.lastName}` })
                  }}
                >
                  {t('family.end')}
                </Button>
              ) : null}
            </div>
          ))}
        </Card>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={t('family.formTitle')}>
        <p className={styles.modalLead}>{t('family.formLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}

        <div className={styles.formGrid}>
          <FormField label={t('family.fields.firstName')} htmlFor={ids.firstName}>
            <Input
              id={ids.firstName}
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.lastName')} htmlFor={ids.lastName}>
            <Input
              id={ids.lastName}
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('family.fields.pesel')}
            htmlFor={ids.pesel}
            hint={t('family.fields.peselHint')}
          >
            <Input
              id={ids.pesel}
              value={form.pesel}
              onChange={(event) => setForm({ ...form, pesel: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.birthDate')} htmlFor={ids.birthDate}>
            <Input
              id={ids.birthDate}
              type="date"
              value={form.birthDate}
              onChange={(event) => setForm({ ...form, birthDate: event.target.value })}
            />
          </FormField>
          {/*
            Kod stopnia pokrewieństwa wchodzi do pola 10 formularza — dwa znaki
            z wykazu ZUS, więc lista, nie wolny tekst.
          */}
          <FormField label={t('family.fields.kinship')} htmlFor={ids.kinship}>
            <Select
              id={ids.kinship}
              value={form.kinshipCode}
              onChange={(event) => setForm({ ...form, kinshipCode: event.target.value })}
            >
              <option value="">{t('family.fields.kinshipPlaceholder')}</option>
              {KINSHIP_CODES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {t(`family.kinship.${entry.code}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('family.fields.disability')}
            htmlFor={ids.disability}
            hint={t('family.fields.disabilityHint')}
          >
            <Select
              id={ids.disability}
              value={form.disabilityCode}
              onChange={(event) => setForm({ ...form, disabilityCode: event.target.value })}
            >
              {DISABILITY_DEGREES.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {t(`family.disability.${entry.code}`)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label={t('family.fields.entitlementFrom')}
            htmlFor={ids.from}
            hint={t('family.fields.entitlementFromHint')}
          >
            <Input
              id={ids.from}
              type="date"
              value={form.entitlementFrom}
              onChange={(event) => setForm({ ...form, entitlementFrom: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('family.fields.declaredAt')}
            htmlFor={ids.declared}
            hint={t('family.fields.declaredAtHint')}
          >
            <Input
              id={ids.declared}
              type="date"
              value={form.declaredAt}
              onChange={(event) => setForm({ ...form, declaredAt: event.target.value })}
            />
          </FormField>
        </div>

        <Checkbox
          label={t('family.fields.sharedHousehold')}
          checked={form.sharedHousehold}
          onChange={(event) => setForm({ ...form, sharedHousehold: event.target.checked })}
        />

        {/*
          Blok IV.B formularza wypełnia się TYLKO wtedy, gdy adres członka
          rodziny jest inny niż adres ubezpieczonego — dlatego pola są puste
          i opcjonalne, a nie kopiowane z akt.
        */}
        <p className={styles.modalLead}>{t('family.addressLead')}</p>
        <div className={styles.formGrid}>
          <FormField label={t('family.fields.postalCode')} htmlFor={ids.postalCode}>
            <Input
              id={ids.postalCode}
              value={form.addressPostalCode}
              onChange={(event) => setForm({ ...form, addressPostalCode: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.city')} htmlFor={ids.city}>
            <Input
              id={ids.city}
              value={form.addressCity}
              onChange={(event) => setForm({ ...form, addressCity: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.street')} htmlFor={ids.street}>
            <Input
              id={ids.street}
              value={form.addressStreet}
              onChange={(event) => setForm({ ...form, addressStreet: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.houseNo')} htmlFor={ids.houseNo}>
            <Input
              id={ids.houseNo}
              value={form.addressHouseNo}
              onChange={(event) => setForm({ ...form, addressHouseNo: event.target.value })}
            />
          </FormField>
          <FormField label={t('family.fields.flatNo')} htmlFor={ids.flatNo}>
            <Input
              id={ids.flatNo}
              value={form.addressFlatNo}
              onChange={(event) => setForm({ ...form, addressFlatNo: event.target.value })}
            />
          </FormField>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            {t('family.cancel')}
          </Button>
          <Button disabled={!addReady || add.isPending} onClick={() => add.mutate()}>
            {add.isPending ? <Spinner label={t('family.saving')} /> : t('family.submit')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={ending !== null}
        onClose={() => setEnding(null)}
        title={t('family.endTitle', { name: ending?.name ?? '' })}
      >
        <p className={styles.modalLead}>{t('family.endLead')}</p>
        {error !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(error)}
          </InfoBanner>
        ) : null}
        <div className={styles.formGrid}>
          <FormField label={t('family.fields.entitlementTo')} htmlFor={ids.endDate}>
            <Input
              id={ids.endDate}
              type="date"
              value={endForm.entitlementTo}
              onChange={(event) => setEndForm({ ...endForm, entitlementTo: event.target.value })}
            />
          </FormField>
          <FormField
            label={t('family.fields.declaredAt')}
            htmlFor={ids.endDeclared}
            hint={t('family.fields.declaredAtHint')}
          >
            <Input
              id={ids.endDeclared}
              type="date"
              value={endForm.declaredAt}
              onChange={(event) => setEndForm({ ...endForm, declaredAt: event.target.value })}
            />
          </FormField>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setEnding(null)}>
            {t('family.cancel')}
          </Button>
          <Button
            disabled={endForm.entitlementTo === '' || endForm.declaredAt === '' || end.isPending}
            onClick={() => end.mutate()}
          >
            {end.isPending ? <Spinner label={t('family.saving')} /> : t('family.endSubmit')}
          </Button>
        </div>
      </Modal>
    </section>
  )
}
